import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, orderBy, limit, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import {
  ChatBubbleLeftRightIcon,
  ChatBubbleBottomCenterTextIcon,
  ClockIcon,
  PencilSquareIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  CheckCircleIcon,
  CheckCircleIcon as CheckCircleOutline,
} from '@heroicons/react/24/outline';
import GrowthAreaPieChart from '../components/GrowthAreaPieChart';
import DiscipleSettingsDrawer from '../components/DiscipleSettingsDrawer';
import useMissionalScaleLevel from '../hooks/useMissionalScaleLevel';
import PrayerRecommendation from '../components/PrayerRecommendation';
import NotesDrawer from '../components/NotesDrawer';
import FaithJourneyDrawer from '../components/FaithJourneyDrawer';
import FaithJourneyPage from './FaithJourneyPage';
import DiscipleHistoryDrawer from '../components/DiscipleHistoryDrawer';
import DiscipleHistory from './DiscipleHistory';
import DiscipleSettings from '../components/DiscipleSettings';
import ImageUpload from '../components/ImageUpload';

// Color map for the icon based on missional scale
const positionColors = {
  '-10': '#0076BE', '-9': '#0079C1', '-8': '#007CC4', '-7': '#007FC7',
  '-6': '#1C8B41', '-5': '#2E943C', '-4': '#3F9D37', '-3': '#51A632',
  '-2': '#62AF2D', '-1': '#74B828', '0': '#85C123', '1': '#96CA1E',
  '2': '#A8D319', '3': '#B9DC14', '4': '#CBE50F', '5': '#DCEe0A',
  '6': '#EEF705', '7': '#F4F807', '8': '#F6F908', '9': '#f7e509'
};

// Missional scale levels and descriptors
const missionalScaleLevels = [
  { value: -10, label: 'Not Interested' },
  { value: -9, label: 'Curious' },
  { value: -8, label: 'Open to Talk' },
  { value: -7, label: 'Exploring' },
  { value: -6, label: 'Seeking' },
  { value: -5, label: 'Considering' },
  { value: -4, label: 'Questioning' },
  { value: -3, label: 'Listening' },
  { value: -2, label: 'Engaging' },
  { value: -1, label: 'Close to Faith' },
  { value: 0, label: 'New Believer' },
  { value: 1, label: 'Growing' },
  { value: 2, label: 'Learning' },
  { value: 3, label: 'Active' },
  { value: 4, label: 'Serving' },
  { value: 5, label: 'Multiplying' },
  { value: 6, label: 'Mentoring' },
  { value: 7, label: 'Leading' },
  { value: 8, label: 'Equipping' },
  { value: 9, label: 'Disciple Maker' },
];

// Cover-flow style FaithJourneySlider
function FaithJourneySlider({ value, onChange, descriptor, phrase, loading }) {
  const [touchStartX, setTouchStartX] = useState(null);

  const currentIdx = missionalScaleLevels.findIndex(l => l.value === value);

  // Handle swipe
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const deltaX = e.changedTouches[0].clientX - touchStartX;
    if (deltaX > 40 && currentIdx > 0) {
      onChange(missionalScaleLevels[currentIdx - 1].value);
    } else if (deltaX < -40 && currentIdx < missionalScaleLevels.length - 1) {
      onChange(missionalScaleLevels[currentIdx + 1].value);
    }
    setTouchStartX(null);
  };

  // Handle click/tap
  const handleClick = (idx) => {
    if (idx !== currentIdx) {
      onChange(missionalScaleLevels[idx].value);
    }
  };

  // Show two on each side
  const visible = [];
  for (let offset = -2; offset <= 2; offset++) {
    const idx = currentIdx + offset;
    if (idx >= 0 && idx < missionalScaleLevels.length) {
      visible.push({ ...missionalScaleLevels[idx], offset });
    }
  }

  return (
    <div
      className="flex flex-col items-center select-none"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="flex items-center justify-center gap-2 w-full" style={{ minHeight: 60 }}>
        {visible.map((item) => {
          const isCurrent = item.offset === 0;
          const scale = isCurrent ? 1.6 : item.offset === -1 || item.offset === 1 ? 1 : 0.8;
          const opacity = isCurrent ? 1 : 0.4 + 0.2 * (2 - Math.abs(item.offset));
          return (
            <div
              key={item.value}
              className={`flex flex-col items-center transition-all duration-200 cursor-pointer`}
              style={{
                transform: `scale(${scale})`,
                opacity,
                zIndex: isCurrent ? 10 : 5 - Math.abs(item.offset),
                margin: isCurrent ? '0 12px' : '0 2px',
                color: isCurrent ? '#fff' : '#e5e7eb',
                fontWeight: isCurrent ? 800 : 500,
                minWidth: 28,
              }}
              onClick={() => handleClick(currentIdx + item.offset)}
            >
              <span className="text-3xl md:text-4xl">{item.value}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-0.5 text-white text-base font-semibold text-center" style={{ minHeight: 20 }}>
        {loading ? 'Loading...' : descriptor}
      </div>
      <div className="text-white text-opacity-80 text-xs mt-0.5 max-w-xs mx-auto overflow-hidden whitespace-nowrap text-ellipsis text-center" style={{ minHeight: 16 }}>
        {phrase}
      </div>
    </div>
  );
}

const DiscipleMenu = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [discipleData, setDiscipleData] = useState(null);
  const [mentorDetails, setMentorDetails] = useState(null);
  const [userAppID, setUserAppID] = useState('');
  const [latestNoteTextForHeader, setLatestNoteTextForHeader] = useState('');
  const [latestNoteTextForButton, setLatestNoteTextForButton] = useState('Track growth and observations');
  const [messagesForChart, setMessagesForChart] = useState([]);
  const [totalMessageCount, setTotalMessageCount] = useState(0);
  const [showNotesDrawer, setShowNotesDrawer] = useState(false);
  const [showFaithJourneyDrawer, setShowFaithJourneyDrawer] = useState(false);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);
  const [prayed, setPrayed] = useState(false);
  const [contentTrackProgress, setContentTrackProgress] = useState(null);
  const [subscribedTrackId, setSubscribedTrackId] = useState(null);
  const [contentTrackName, setContentTrackName] = useState(null);
  const [contentTrack, setContentTrack] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Use the new hook for missional scale
  const { currentLevel, descriptor, phrase, setLevel, loading: scaleLoading } = useMissionalScaleLevel(discipleId, userAppID);

  // Fetch user's appID
  useEffect(() => {
    const fetchUserAppID = async () => {
      if (!currentUser) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          setUserAppID(userDoc.data().appID);
        }
      } catch (error) {
        console.error('Error fetching userAppID:', error);
      }
    };

    fetchUserAppID();
  }, [currentUser]);

  // Fetch disciple, mentor details, latest note, and messages
  useEffect(() => {
    const fetchData = async () => {
      if (!discipleId || !userAppID) return;
      setLoading(true);
      
      // Reset content track state
      setSubscribedTrackId(null);
      setContentTrackProgress(null);
      setContentTrackName(null);
      setContentTrack(null);

      try {
        const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', userAppID);

        // Fetch disciple and mentorDetails first
        const [discipleDoc, mentorDetailsDoc] = await Promise.all([
          getDoc(doc(db, 'disciples', discipleId)),
          getDoc(mentorDetailsRef)
        ]);

        // Only fetch subcollections if mentorDetails exists
        let latestNoteSnapshot = { empty: true, docs: [] };
        let messagesSnapshot = { docs: [] };
        let smsMessagesSnapshot = { docs: [] };

        if (mentorDetailsDoc.exists()) {
          try {
            const notesRef = collection(mentorDetailsRef, 'notes');
            const messagesRef = collection(mentorDetailsRef, 'messages');
            const smsMessagesRef = collection(mentorDetailsRef, 'messagesSMS');

            const latestNoteQuery = query(notesRef, orderBy('createdAt', 'desc'), limit(1));
            const messagesQuery = query(messagesRef);
            const smsMessagesQuery = query(smsMessagesRef);

            // Fetch subcollections with individual error handling
            [latestNoteSnapshot, messagesSnapshot, smsMessagesSnapshot] = await Promise.all([
              getDocs(latestNoteQuery).catch(err => {
                // Silently handle permission errors
                const errorCode = err?.code;
                if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                  console.warn('Error fetching notes:', err);
                }
                return { empty: true, docs: [] };
              }),
              getDocs(messagesQuery).catch(err => {
                // Silently handle permission errors
                const errorCode = err?.code;
                if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                  console.warn('Error fetching messages:', err);
                }
                return { docs: [] };
              }),
              getDocs(smsMessagesQuery).catch(err => {
                // Silently handle permission errors
                const errorCode = err?.code;
                if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                  console.warn('Error fetching SMS messages:', err);
                }
                return { docs: [] };
              })
            ]);
          } catch (subcollectionError) {
            // Silently handle permission errors
            const errorCode = subcollectionError?.code;
            if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
              console.warn('Error accessing subcollections (non-fatal):', subcollectionError);
            }
            // Continue with empty snapshots - page will still render
          }
        }

        if (discipleDoc.exists()) {
          setDiscipleData(discipleDoc.data());
        }

        // Process Mentor Details (store frequency)
        let frequency = 'N/A'; // Declare frequency in the outer scope
        if (mentorDetailsDoc.exists()) {
          const details = mentorDetailsDoc.data();
          setMentorDetails(details);
          // Update frequency if settings exist
          frequency = details.reminderSettings?.frequency || 'N/A';
          
          // Load content track subscription (check both subscribedContentTrackId field and subscribedTracks subcollection)
          const trackId = details.subscribedContentTrackId;
          if (trackId) {
            setSubscribedTrackId(trackId);
            // Load content track details and progress
            try {
              const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
              if (trackDoc.exists()) {
                const trackData = trackDoc.data();
                setContentTrackName(trackData.name || 'Content Track');
                setContentTrack({ id: trackDoc.id, ...trackData });
                
                // Load content track progress
                const progress = details.contentTrackProgress;
                if (progress) {
                  setContentTrackProgress(progress);
                } else {
                  // Calculate progress if not in mentorDetails
                  try {
                    const resourcesRef = collection(db, 'contentTracks', trackId, 'resources');
                    const resourcesSnap = await getDocs(resourcesRef);
                    const publishedResources = resourcesSnap.docs
                      .map(d => ({ id: d.id, ...d.data() }))
                      .filter(r => r.status === 'published' || !r.status);
                    
                    const sentIds = [];
                    const progressData = {
                      trackId,
                      totalPublishedResources: publishedResources.length,
                      sentResourceIds: sentIds,
                      sentCount: sentIds.length,
                      remainingCount: publishedResources.length - sentIds.length,
                      completed: publishedResources.length > 0 && sentIds.length >= publishedResources.length,
                    };
                    setContentTrackProgress(progressData);
                  } catch (resourceError) {
                    // Silently handle permission errors for content track resources
                    const errorCode = resourceError?.code;
                    if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                      console.error('Error loading content track resources:', resourceError);
                    }
                  }
                }
              }
            } catch (error) {
              // Silently handle permission errors for content tracks
              const errorCode = error?.code;
              if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                console.error('Error loading content track:', error);
              }
            }
          } else {
            // Check subscribedTracks subcollection as fallback
            try {
              const subscribedTracksRef = collection(mentorDetailsRef, 'subscribedTracks');
              const subscribedSnap = await getDocs(subscribedTracksRef);
              if (!subscribedSnap.empty) {
                // Use first subscribed track
                const firstTrack = subscribedSnap.docs[0];
                const trackId = firstTrack.id;
                setSubscribedTrackId(trackId);
                
                // Load track name
                const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));
                if (trackDoc.exists()) {
                  const trackData = trackDoc.data();
                  setContentTrackName(trackData.name || 'Content Track');
                  setContentTrack({ id: trackDoc.id, ...trackData });
                }
              }
            } catch (error) {
              // Silently handle permission errors for subscribedTracks
              const errorCode = error?.code;
              if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
                console.error('Error checking subscribedTracks:', error);
              }
            }
          }
        }

        // Process Latest Note (store for both header and button)
        if (!latestNoteSnapshot.empty) {
          const latestNote = latestNoteSnapshot.docs[0].data().note;
          const truncatedForButton = latestNote.length > 50 
            ? latestNote.slice(0, 50) + '...'
            : latestNote;
          setLatestNoteTextForButton(truncatedForButton);
        } else {
          setLatestNoteTextForButton('Track growth and observations');
        }

        // Process Messages (calculate count and prep for chart)
        const emailMessages = messagesSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() })) 
            .filter(msg => !msg.placeholder && msg.verseReference); 
            
        const smsMessages = smsMessagesSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() })) 
            .filter(msg => !msg.placeholder && msg.verseReference); 

        const allValidMessages = [...emailMessages, ...smsMessages]; 
        const totalCount = allValidMessages.length; 
        setTotalMessageCount(totalCount);
        
        // Add console log for the calculated count
        console.log('Calculated Total Message Count:', totalCount, { emailCount: emailMessages.length, smsCount: smsMessages.length });

        // Use allValidMessages for the chart data as well
        const relevantMessages = allValidMessages.filter(msg => 
          msg.growthArea || msg.verse?.growthArea
        );
        setMessagesForChart(relevantMessages);

        // Sets the header text using the filtered count and correctly scoped frequency
        const freqText = frequency !== 'N/A' ? `${frequency} day${frequency === 1 ? '' : 's'}` : 'Not set'; // Fixed pluralization
        setLatestNoteTextForHeader(`Frequency: ${freqText}  ·  ${totalCount} total message${totalCount !== 1 ? 's' : ''}`);

      } catch (error) {
        // Check if it's a permission error - don't show error message for those
        const errorCode = error?.code;
        if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
          console.error('Error fetching data:', error);
          setLatestNoteTextForHeader('Could not load details');
        } else {
          // Silently handle permission errors - page will render with available data
          setLatestNoteTextForHeader('');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [discipleId, userAppID, refreshTrigger]);

  // Determine the icon color before defining menuItems
  const currentScale = mentorDetails?.missionalScale;
  const defaultIconColor = '#6b7280'; // Default gray color
  const faithJourneyIconColor = 
    (currentScale !== undefined && currentScale !== null && positionColors[String(currentScale)]) 
    ? positionColors[String(currentScale)] 
    : defaultIconColor;

  const primaryAction = {
    title: "Send a Message",
    description: "Choose from personalized resource suggestions",
    icon: <ChatBubbleLeftRightIcon className="h-7 w-7" />,
    onClick: () => navigate(`/suggested-verses/${discipleId}`),
  };

  const menuItems = [
    {
      title: "Listen, Connect & Help",
      description: "Send a survey question, add notes from their response, then share the Word",
      icon: <ChatBubbleBottomCenterTextIcon className="h-6 w-6" />,
      onClick: () => navigate(`/view-disciple/${discipleId}/listen-connect-help`),
    },
    {
      title: "Track Progress - Message History",
      description: `View past messages sent to ${discipleData?.name || 'your disciple'}`,
      icon: <ClockIcon className="h-6 w-6" />,
      onClick: () => navigate(`/disciple-history/${discipleId}`),
    },
    {
      title: "View Faith Journey",
      description: `Current Level: ${currentScale ?? 'Not Set'}`,
      // Wrap icon in a div with dynamic background color
      icon: (
        <div 
          className="inline-flex p-1 rounded-md"
          style={{ backgroundColor: faithJourneyIconColor }}
        >
          <ChartBarIcon className="h-6 w-6 text-white" /> 
        </div>
      ),
      onClick: () => navigate(`/faith-journey/${discipleId}`),
    },
  ];

  // Update mentoring notes item description dynamically
  const mentoringNotesItem = {
    title: "Add an Observation",
    description: latestNoteTextForButton,
    icon: <PencilSquareIcon className="h-6 w-6" />,
    onClick: () => navigate(`/mentoring-notes/${discipleId}`),
  };

  // Filter out the notes item from the main list
  const remainingMenuItems = menuItems.filter(item => item.title !== "Mentoring Notes");

  // Add console log here to check the data being passed
  console.log('Messages being passed to Pie Chart:', messagesForChart);

  const handlePrayed = async () => {
    setPrayed(true);
    // Mark as prayed in Firestore (prayerLog subcollection)
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    try {
      await setDoc(
        doc(db, 'disciples', discipleId, 'prayerLog', today),
        {
          prayed: true,
          prayedAt: new Date(),
          userId: currentUser.uid,
        },
        { merge: true }
      );
    } catch (err) {
      // Silently handle permission errors for prayer log
      const errorCode = err?.code;
      if (errorCode !== 'permission-denied' && errorCode !== 'firestore/permission-denied') {
        console.error('Error marking prayer as prayed:', err);
      }
      // Still keep the UI state as prayed even if write fails
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Curved Gradient Header */}
      <div
        className="relative bg-gradient-to-br pb-12 rounded-b-3xl shadow-lg"
        style={{
          backgroundImage:
            'linear-gradient(125deg, #0076BE 0%, #5ABA47 78%, #f7e509 100%)'
        }}
      >
        {/* Settings Cog in top right */}
        <button
          onClick={() => setShowSettingsDrawer(true)}
          className="absolute top-6 right-6 z-20 p-2 rounded-full bg-white bg-opacity-30 hover:bg-opacity-50 transition"
          title="Settings"
          style={{ boxShadow: '0 2px 8px 0 rgba(0,0,0,0.08)' }}
        >
          <Cog6ToothIcon className="h-8 w-8 text-white" />
        </button>
        <div className="flex flex-col items-center pt-10">
          {/* Disciple Image - now smaller */}
          <div className="h-40 w-40 rounded-full bg-white shadow-lg flex items-center justify-center mb-1 border-4"
            style={{ borderColor: '#5bb6e6', borderWidth: 3 }}>
            <ImageUpload
              currentImage={mentorDetails?.photoURL}
              path={`disciples/${discipleId}/mentorDetails/${userAppID}/profile`}
              onUploadSuccess={async (url) => {
                await updateDoc(
                  doc(db, 'disciples', discipleId, 'mentorDetails', userAppID),
                  { photoURL: url }
                );
                setMentorDetails((prev) => ({ ...prev, photoURL: url }));
              }}
              className="w-full h-full"
            />
          </div>
          <h1 className="text-2xl font-bold text-white mt-2">{discipleData?.name || 'Disciple'}</h1>
          {/* Next Reminder under name */}
          {mentorDetails?.reminderSettings?.nextReminderDate && (
            <div className="text-white text-opacity-60 text-xs mt-1">
              Next Reminder: {new Date(mentorDetails.reminderSettings.nextReminderDate).toLocaleDateString()}
            </div>
          )}
          {/* Start Conversation - primary CTA (Apple-style glass button) */}
          {!(discipleData?.isChild === true && (discipleData?.hasDevice === false || discipleData?.hasDevice === undefined)) && (
            <div className="w-full px-6 mt-3">
              <button
                onClick={() => navigate(`/view-disciple/${discipleId}/listen-connect-help`)}
                className="w-full flex flex-col items-center justify-center rounded-2xl py-4 text-base font-semibold transition-all duration-200 relative overflow-hidden min-h-[70px] border border-white/30 shadow-xl hover:shadow-2xl hover:scale-[1.01] active:scale-[0.99]"
                style={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
                }}
              >
                <div className="flex items-center justify-center mb-1">
                  <ChatBubbleBottomCenterTextIcon className="h-6 w-6 mr-2 text-white" />
                  <span className="text-lg font-bold text-white">Start Conversation</span>
                </div>
                <span className="text-xs font-normal text-white/90 max-w-[20rem] text-center">Send a question, add notes, then share a verse or content.</span>
              </button>
            </div>
          )}
        </div>
        {/* Faith Journey Cover-Flow Slider */}
        <div className="flex flex-col items-center mt-6">
          <FaithJourneySlider value={currentLevel ?? 0} onChange={setLevel} descriptor={descriptor} phrase={phrase} loading={scaleLoading} />
        </div>
      </div>

      {/* Horizontal Stats Row as Action Buttons */}
      <div className="flex justify-around mt-[-32px] z-10 gap-2">
        {/* History Button */}
        <button
          onClick={() => setShowHistoryDrawer(true)}
          className="bg-white rounded-xl shadow-md px-4 py-2 flex flex-col items-center justify-center border-2 focus:ring-2 focus:ring-blue-200 transition"
          style={{ borderColor: '#0076BE', minWidth: 80 }}
        >
          <span className="flex items-center justify-center font-bold h-7" style={{ color: '#0076BE' }}>
            <ChartBarIcon className="h-5 w-5 mr-1" />
            {totalMessageCount ?? 0}
          </span>
          <span className="text-xs text-gray-500">History</span>
        </button>
        {/* Add Observation Button */}
        <button
          onClick={() => setShowNotesDrawer(true)}
          className="bg-white rounded-xl shadow-md px-4 py-2 flex flex-col items-center justify-center border-2 border-gray-200 focus:ring-2 focus:ring-blue-200 transition"
          style={{ borderColor: '#777777', minWidth: 80 }}
        >
          <span className="font-bold text-gray-500 flex items-center justify-center h-7">
            <PencilSquareIcon className="h-5 w-5 mr-1" />
          </span>
          <span className="text-xs text-gray-500">Add Observation</span>
        </button>
        {/* Faith Journey Button */}
        <button
          onClick={() => setShowFaithJourneyDrawer(true)}
          className="bg-white rounded-xl shadow-md px-4 py-2 flex flex-col items-center justify-center border-2 focus:ring-2 transition"
          style={{ borderColor: positionColors[String(currentLevel ?? 0)] || '#5ABA47', minWidth: 80 }}
        >
          <span className="font-bold flex items-center justify-center h-7" style={{ color: positionColors[String(currentLevel ?? 0)] || '#5ABA47' }}>
            <ClockIcon className="h-5 w-5 mr-1" />
          </span>
          <span className="text-xs text-gray-500">Faith Journey</span>
        </button>
      </div>
      {/* Prayer Recommendation - in the white space beneath the history bullet */}
      <div className="px-4 mt-4">
        <PrayerRecommendation discipleId={discipleId} mentorId={userAppID} />
      </div>

      {/* Settings Drawer */}
      <DiscipleSettingsDrawer
        open={showSettingsDrawer}
        onClose={() => setShowSettingsDrawer(false)}
        discipleData={discipleData}
        mentorDetails={mentorDetails}
      >
        {mentorDetails && userAppID && discipleData && (
          <DiscipleSettings
            isOpen={true}
            onClose={() => setShowSettingsDrawer(false)}
            discipleId={discipleId}
            currentUser={currentUser}
            initialData={discipleData}
            mentorDetails={mentorDetails}
            userAppID={userAppID}
            onSettingsSaved={() => {
              setShowSettingsDrawer(false);
              setRefreshTrigger(prev => prev + 1);
            }}
          />
        )}
      </DiscipleSettingsDrawer>

      {/* Notes Drawer */}
      <NotesDrawer
        open={showNotesDrawer}
        onClose={() => setShowNotesDrawer(false)}
        discipleId={discipleId}
        userAppID={userAppID}
        onNoteAdded={() => setShowNotesDrawer(false)}
        discipleData={discipleData}
        mentorDetails={mentorDetails}
      />
      {/* Faith Journey Drawer */}
      <FaithJourneyDrawer
        open={showFaithJourneyDrawer}
        onClose={() => setShowFaithJourneyDrawer(false)}
        discipleData={discipleData}
        mentorDetails={mentorDetails}
      >
        <FaithJourneyPage />
      </FaithJourneyDrawer>
      {/* History Drawer */}
      <DiscipleHistoryDrawer
        open={showHistoryDrawer}
        onClose={() => setShowHistoryDrawer(false)}
        discipleData={discipleData}
        mentorDetails={mentorDetails}
      >
        <DiscipleHistory />
      </DiscipleHistoryDrawer>
    </div>
  );
};

export default DiscipleMenu; 